import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { 
  LayoutDashboard, 
  Newspaper, 
  Settings, 
  LogOut, 
  ChevronRight,
  Plus,
  FileText,
  Library,
  Shield,
  Bell,
  Menu,
  X
} from "lucide-react";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RADKO_LOGO = "https://customer-assets.emergentagent.com/job_2f120071-ff5a-4fce-a9d2-966a29686f9d/artifacts/083jbaq1_Design%20sans%20titre-fotor-bg-remover-20260313163332.png";

export const Layout = ({ children }) => {
  const { user, logout, getAuthHeader, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [medias, setMedias] = useState([]);
  const [loadingMedias, setLoadingMedias] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchMedias = async () => {
      try {
        const response = await axios.get(`${API}/medias`, getAuthHeader());
        setMedias(response.data);
      } catch (error) {
        console.error("Erreur chargement médias:", error);
      } finally {
        setLoadingMedias(false);
      }
    };
    fetchMedias();
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Déconnexion réussie");
  };

  const seedMedias = async () => {
    try {
      await axios.post(`${API}/seed-medias`, {}, getAuthHeader());
      const response = await axios.get(`${API}/medias`, getAuthHeader());
      setMedias(response.data);
      toast.success("Médias initialisés");
    } catch (error) {
      toast.error("Erreur lors de l'initialisation");
    }
  };

  const getMediaTagClass = (slug) => {
    switch (slug) {
      case "generaliste": return "bg-white/20 text-white";
      case "outre-mer": return "bg-[#F36C21]/30 text-orange-200";
      case "afrique-matin": return "bg-red-500/20 text-red-200";
      case "omerta": return "bg-white/10 text-blue-200";
      case "infomatin": return "bg-[#3A7BD5]/30 text-blue-200";
      default: return "bg-white/20 text-white";
    }
  };

  const navLink = (to, icon, label, badge) => {
    const Icon = icon;
    const isActive = location.pathname === to;
    return (
      <Link
        to={to}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors mb-1 ${
          isActive 
            ? "bg-white/15 text-white" 
            : "text-blue-200 hover:text-white hover:bg-white/10"
        }`}
        data-testid={`nav-${to.replace('/', '') || 'dashboard'}`}
      >
        <Icon className="w-4 h-4" strokeWidth={1.5} />
        <span className="text-sm font-medium">{label}</span>
        {badge}
      </Link>
    );
  };

  return (
    <div className="min-h-screen flex">
      {/* Mobile top bar — visible only on < md */}
      <div 
        className="fixed top-0 left-0 right-0 z-30 flex md:hidden items-center justify-between px-4 py-3"
        style={{ background: 'linear-gradient(135deg, #1F4E8C 0%, #163a6a 100%)' }}
        data-testid="mobile-header"
      >
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/10 p-1.5"
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </Button>
        <Link to="/">
          <img src={RADKO_LOGO} alt="RadKo.ai" className="h-8" />
        </Link>
        <div className="w-9" />
      </div>

      {/* Backdrop — visible on mobile when sidebar is open */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-35 md:hidden"
          style={{ zIndex: 35 }}
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 bottom-0 left-0 z-40 w-[280px] border-r-4 border-[#F36C21] overflow-y-auto
          transition-transform duration-300 ease-in-out
          md:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ background: 'linear-gradient(180deg, #1F4E8C 0%, #163a6a 100%)' }}
        data-testid="sidebar"
      >
        {/* Mobile close button */}
        <div className="flex items-center justify-end p-2 md:hidden">
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10 p-1.5"
            onClick={() => setSidebarOpen(false)}
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="bg-white p-4 md:p-6">
          <Link to="/" className="block">
            <img src={RADKO_LOGO} alt="RadKo.ai" className="w-full h-auto" />
          </Link>
          <p className="text-sm text-[#1F4E8C] text-center font-medium mt-3">Générons et publions le futur !</p>
        </div>
        
        <nav className="p-4">
          {navLink("/", LayoutDashboard, "Tableau de bord")}
          {navLink("/library", Library, "Bibliothèque", 
            <span className="ml-auto text-xs bg-[#F36C21] text-white px-1.5 py-0.5 rounded">Auto</span>
          )}
          {navLink("/alerts", Bell, "Alertes",
            <span className="ml-auto text-xs bg-red-500 text-white px-1.5 py-0.5 rounded animate-pulse">Live</span>
          )}
          {isAdmin() && navLink("/admin", Shield, "Administration",
            <span className="ml-auto text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded">Admin</span>
          )}
        </nav>
        
        <div className="px-4 mb-2">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-blue-300 uppercase tracking-wider">
              Médias
            </span>
            {medias.length === 0 && !loadingMedias && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-blue-200 hover:text-white"
                onClick={seedMedias}
                data-testid="seed-medias-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                Initialiser
              </Button>
            )}
          </div>
          
          <ScrollArea className="h-[200px]">
            {loadingMedias ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 bg-white/10 rounded-md animate-pulse" />
                ))}
              </div>
            ) : medias.length === 0 ? (
              <p className="text-xs text-blue-300 px-3 py-2">
                Aucun média configuré
              </p>
            ) : (
              <div className="space-y-1">
                {medias.map((media) => (
                  <Link
                    key={media.id}
                    to={`/media/${media.id}`}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-md transition-colors group ${
                      location.pathname === `/media/${media.id}`
                        ? "bg-white/15 text-white"
                        : "text-blue-200 hover:text-white hover:bg-white/10"
                    }`}
                    data-testid={`nav-media-${media.slug}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-sm font-medium truncate">{media.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${getMediaTagClass(media.slug)}`}>
                        {media.article_count}
                      </span>
                      <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <Link
            to="/settings"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors mb-2 ${
              location.pathname === "/settings"
                ? "bg-white/15 text-white"
                : "text-blue-200 hover:text-white hover:bg-white/10"
            }`}
            data-testid="nav-settings"
          >
            <Settings className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm font-medium">Paramètres</span>
          </Link>
          
          <div className="flex items-center justify-between px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-blue-200 truncate">{user?.email}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-blue-200 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </aside>
      
      {/* Main content — offset by sidebar width on md+ */}
      <main className="flex-1 md:ml-[280px] min-h-screen bg-background pt-14 md:pt-0">
        {children}
      </main>
    </div>
  );
};
